import { either, errorToReactLeft } from 'utils/fp';
import { queryKeys, useMutationTE } from 'utils/react-query';
import { acceptInvitationTE, declineInvitationTE } from 'api/organizations';
import { ReactLeft } from 'utils/uiStates/uiStates';
import cogoToast from 'components/ToastMessage/ToastMessage';
import { useAuthState } from 'modules/Auth/AuthContext';
import { QueryClient, useQueryClient } from 'react-query';

type TCachedOrgInvitationQuery = either.Either<ReactLeft, CF.API.Organizations.OrgMemberInvite[]> | undefined;

// No need to refetch the query, simply remove it from the react-query cache.
// When the query is refetched, the accepted/declined invite won't be there anyway.
function removeInviteFromCachedQuery({
  queryClient,
  invitation,
  userId,
}: {
  queryClient: QueryClient;
  invitation?: CF.API.Organizations.OrgMemberInvite;
  userId: string;
}) {
  const queryKey = [queryKeys.UserOrganizationInvitations, { userId }];

  if (!queryClient.getQueryData(queryKey) || !invitation) {
    return;
  }

  queryClient.setQueryData<TCachedOrgInvitationQuery>(queryKey, (invitations) => {
    if (invitations && either.isRight(invitations)) {
      const removedInviteList = either.right(invitations.right.filter((keepInvite) => keepInvite.id !== invitation.id));
      return removedInviteList;
    }

    return invitations;
  });
}

export function useAcceptOrgInvitation(onSuccessCallback?: () => void) {
  const { authData } = useAuthState();
  // const { changeUserAccountById } = useAuthActions();
  const queryClient = useQueryClient();

  return useMutationTE(
    (invitation?: CF.API.Organizations.OrgMemberInvite) => {
      if (!invitation) {
        return Promise.reject(new Error('Invitation is invalid')) as Promise<either.Either<ReactLeft, CF.API.BaseResponse>>;
      }
      return acceptInvitationTE({ invitationId: invitation.id, userId: authData?.user_id || '' }, errorToReactLeft)();
    },
    {
      onSuccess: (_, invitation) => {
        cogoToast.success('Invitation accepted.');
        queryClient.invalidateQueries([queryKeys.Organizations]);
        // changeUserAccountById(invitation!.organization.id);
        removeInviteFromCachedQuery({ queryClient, invitation, userId: authData?.user_id || '' });
        onSuccessCallback?.();
      },
      onError: () => {
        cogoToast.error('Failed to accept the invitation.');
      },
    },
  );
}

export function useDeclineOrgInvitation(onSuccessCallback?: () => void) {
  const { authData } = useAuthState();
  const queryClient = useQueryClient();

  return useMutationTE(
    (invitation?: CF.API.Organizations.OrgMemberInvite) => {
      if (!invitation) {
        return Promise.reject(new Error('Invitation is invalid')) as Promise<either.Either<ReactLeft, CF.API.BaseResponse>>;
      }
      return declineInvitationTE({ invitationId: invitation.id, userId: authData?.user_id || '' }, errorToReactLeft)();
    },
    {
      onSuccess: (_, invitation) => {
        cogoToast.success('Invitation declined.');
        removeInviteFromCachedQuery({ queryClient, invitation, userId: authData?.user_id || '' });
        onSuccessCallback?.();
      },
      onError: () => {
        cogoToast.error('Failed to decline the invitation.');
      },
    },
  );
}
